import { useState } from 'react';
import { usePendingUsers } from '@/hooks/use-pending-users';
import { useMenuRoleSetList } from '@/hooks/useMenuRoleSet';
import { useOrganization } from '@/contexts/organization-context';
import type { PendingUserResponse } from '@/lib/api/data-contracts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { UserCheck, Users } from 'lucide-react';

export function UserApprovalPage() {
  const { users, loading, approveUser, rejectUser } = usePendingUsers();
  const { data: roleSetData } = useMenuRoleSetList();
  const roleSets = roleSetData ?? [];
  const { getCompaniesByType, getOrganizationsByCompany } = useOrganization();

  const [approveTarget, setApproveTarget] = useState<PendingUserResponse | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingUserResponse | null>(null);
  const [approveForm, setApproveForm] = useState<{ compCd: string; orgCd: string; roleSetId: string }>({
    compCd: '',
    orgCd: '',
    roleSetId: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const companies = getCompaniesByType();
  const organizations = approveForm.compCd ? getOrganizationsByCompany(approveForm.compCd) : [];

  const handleOpenApprove = (user: PendingUserResponse) => {
    const compCd = user.companyCode ?? '';
    const orgsForCompany = compCd ? getOrganizationsByCompany(compCd) : [];
    setApproveTarget(user);
    setApproveForm({
      compCd,
      // 하위 조직이 없으면 회사 코드 자체를 소속으로 사용
      orgCd: orgsForCompany.length > 0 ? (user.organizationCode ?? '') : compCd,
      roleSetId: '',
    });
  };

  const handleApprove = async () => {
    if (!approveTarget?.id) return;
    if (!approveForm.compCd || !approveForm.orgCd) {
      toast({ title: '소속 회사와 조직을 선택해주세요.', variant: 'destructive' });
      return;
    }
    if (!approveForm.roleSetId) {
      toast({ title: '역할세트를 선택해주세요.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await approveUser(approveTarget.id, {
        compCd: approveForm.compCd,
        orgCd: approveForm.orgCd,
        roleSetId: Number(approveForm.roleSetId),
      });
      toast({ title: '승인되었습니다.', description: `${approveTarget.name} 사용자가 승인되었습니다.` });
      setApproveTarget(null);
    } catch {
      toast({ title: '승인 실패', description: '승인 처리 중 오류가 발생했습니다.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget?.id) return;
    setSubmitting(true);
    try {
      await rejectUser(rejectTarget.id);
      toast({ title: '거부되었습니다.', description: `${rejectTarget.name} 사용자가 거부되었습니다.` });
      setRejectTarget(null);
    } catch {
      toast({ title: '거부 실패', description: '거부 처리 중 오류가 발생했습니다.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <div className='p-3 space-y-3'>
      <Breadcrumb items={menuBreadcrumbItems('user-approval')} />

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <UserCheck className='size-5' />
                가입 승인 관리
              </CardTitle>
              <CardDescription>가입을 요청한 사용자를 승인하거나 거부합니다.</CardDescription>
            </div>
            {users.length > 0 && (
              <Badge variant='secondary'>{users.length}명 대기 중</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className='flex items-center justify-center py-12 text-muted-foreground text-sm'>
              불러오는 중...
            </div>
          ) : users.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-16 text-muted-foreground gap-3'>
              <Users className='size-12 opacity-30' />
              <p className='text-sm'>승인 대기 중인 사용자가 없습니다.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>이름</TableHead>
                  <TableHead>아이디</TableHead>
                  <TableHead>이메일</TableHead>
                  <TableHead>가입일</TableHead>
                  <TableHead className='text-right'>액션</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className='font-medium'>{user.name ?? '-'}</TableCell>
                    <TableCell>{user.loginId ?? '-'}</TableCell>
                    <TableCell>{user.email ?? '-'}</TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell className='text-right'>
                      <div className='flex justify-end gap-2'>
                        <Button size='sm' onClick={() => handleOpenApprove(user)}>
                          승인
                        </Button>
                        <Button
                          size='sm'
                          variant='destructive'
                          onClick={() => setRejectTarget(user)}
                        >
                          거부
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* 승인 다이얼로그 */}
      <Dialog open={!!approveTarget} onOpenChange={open => { if (!open) setApproveTarget(null); }}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>사용자 승인</DialogTitle>
            <DialogDescription>
              {approveTarget?.name} 사용자의 소속 정보를 입력하세요.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>소속 회사 *</label>
              <Select
                value={approveForm.compCd}
                onValueChange={value => {
                  const orgs = getOrganizationsByCompany(value);
                  setApproveForm(prev => ({ ...prev, compCd: value, orgCd: orgs.length > 0 ? '' : value }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='회사 선택' />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.compCd!} value={c.compCd!}>
                      {c.orgName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>소속 조직 *</label>
              {approveForm.compCd && organizations.length === 0 ? (
                <div className='flex h-9 items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground'>
                  회사 직속 (하위 조직 없음)
                </div>
              ) : (
                <Select
                  value={approveForm.orgCd}
                  onValueChange={value => setApproveForm(prev => ({ ...prev, orgCd: value }))}
                  disabled={!approveForm.compCd}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={approveForm.compCd ? '조직 선택' : '회사를 먼저 선택하세요'} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map(o => (
                      <SelectItem key={o.orgCd!} value={o.orgCd!}>
                        {o.orgName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className='space-y-1.5'>
              <label className='text-sm font-medium'>역할세트 *</label>
              <Select
                value={approveForm.roleSetId}
                onValueChange={value => setApproveForm(prev => ({ ...prev, roleSetId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder='역할세트 선택' />
                </SelectTrigger>
                <SelectContent>
                  {roleSets.map(rs => (
                    <SelectItem key={rs.id} value={String(rs.id)}>
                      {rs.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setApproveTarget(null)} disabled={submitting}>
              취소
            </Button>
            <Button onClick={handleApprove} disabled={submitting || !approveForm.roleSetId}>
              승인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 거부 확인 다이얼로그 */}
      <AlertDialog open={!!rejectTarget} onOpenChange={open => { if (!open) setRejectTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>가입 거부</AlertDialogTitle>
            <AlertDialogDescription>
              {rejectTarget?.name} 사용자의 가입 요청을 거부하시겠습니까? 이 작업은 되돌릴 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={submitting}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              거부
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
